'use client';

import clsx from 'clsx';
import { useState, useEffect, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Button, { ButtonType } from '../../Button/Button';
import Icon from '../../Icon';
import { usePathname, useRouter } from '@/i18n/navigation';
import { Locale } from '@/i18n/routing';
import styles from './LanguageDropdown.module.scss';

export interface Locales {
  id: number;
  documentId: string;
  name: string;
  code: Locale;
}

const LanguageDropdown = ({ isLight, locales }: { isLight: boolean, locales: Locales[] }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLocale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('LocaleSwitcher');

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const switchLocale = (newLocale: string) => {
    if (newLocale !== currentLocale) {
      router.replace(pathname, { locale: newLocale });
      router.refresh();
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={clsx(styles.languageDropdown, isDropdownOpen && styles.open)}
    >
      <Button
        type={isLight ? ButtonType.TEXT_DARK : ButtonType.TEXT_LIGHT}
        className={styles.languageButton}
        onClick={toggleDropdown}
      >
        <span>{currentLocale}</span>
        <Icon
          name='expand_more'
          className={styles.dropdownArrow}
        />
      </Button>

      {isDropdownOpen && (
        <div className={styles.dropdownMenu}>
            {locales.map((locale) => (
              <button
                type='button'
                key={locale?.code}
                className={styles.dropdownItem}
                onClick={() => switchLocale(locale?.code)}
              >
                <span>{t('locale', { locale: locale?.code })}</span>
                <Icon
                  name='arrow_forward'
                  className={styles.arrow}
                />
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
